import {
  startTransition,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWebSocket } from '../../../context/WebSocketContext';
import { useToastContext } from '../../../context/ToastProvider';
import { getVideoToken } from '../services/call.service';
import { getUserId } from '../../../core/auth/token';
import type {
  IncomingCallState,
  WsInboundMessage,
} from '../types/call.types';

export interface UseIncomingCallReturn {
  incomingCall: IncomingCallState | null;
  showIncomingModal: boolean;
  isAccepting: boolean;
  acceptCall: () => void;
  rejectCall: () => void;
}

export const useIncomingCall = (): UseIncomingCallReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lastMessage, sendMessage } = useWebSocket();
  const { showError } = useToastContext();
  const lastMessageKeyRef = useRef<string | null>(null);
  const lastResetTimeRef = useRef<number>(0);
  // Holds the active Web Notification so we can close it on dismiss
  const callNotificationRef = useRef<Notification | null>(null);

  const [incomingCall, setIncomingCall] =
    useState<IncomingCallState | null>(null);
  const [showIncomingModal, setShowIncomingModal] =
    useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  const deferStateUpdate = useCallback((update: () => void) => {
    queueMicrotask(() => {
      startTransition(update);
    });
  }, []);

  /** Fire a browser Web Notification for the incoming call */
  const showCallNotification = useCallback((callerName: string) => {
    if (!('Notification' in window)) return;

    const fire = () => {
      // Close any previous notification first
      callNotificationRef.current?.close();
      const n = new Notification('Incoming Call', {
        body: `${callerName} is calling you`,
        icon: '/favicon.ico',
        tag: 'incoming-call',   // replaces any existing notification with the same tag
        requireInteraction: true, // keeps it on screen until user interacts
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
      callNotificationRef.current = n;
    };

    if (Notification.permission === 'granted') {
      fire();
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((perm) => {
        if (perm === 'granted') fire();
      });
    }
  }, []);

  const resetIncomingState = useCallback(() => {
    // Dismiss the Web Notification if it's still showing
    callNotificationRef.current?.close();
    callNotificationRef.current = null;
    setIncomingCall(null);
    setShowIncomingModal(false);
    setIsAccepting(false);
    lastResetTimeRef.current = Date.now();
  }, []);

  // Reset incoming state when navigating away from call page
  useEffect(() => {
    if (!location.pathname.startsWith('/call')) {
      resetIncomingState();
    }
  }, [location.pathname, resetIncomingState]);

  useEffect(() => {
    if (!showIncomingModal) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';   // required for Chrome/Edge
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [showIncomingModal]);

  const navigateToResolvedCall = useCallback((
    callerId: string,
    callSessionId: string,
    sessionId: string,
    resolvedToken: string
  ) => {
    navigate(`/call/${callerId}`, {
      state: {
        callSessionId,
        sessionId,
        token: resolvedToken,
        deviceId: callerId,
      },
    });
    resetIncomingState();
  }, [navigate, resetIncomingState]);

  useEffect(() => {
    if (!lastMessage) return;
    const message = lastMessage as WsInboundMessage;
    const type = message.type;
    const callSessionId =
      'callSessionId' in message ? message.callSessionId ?? '' : '';
    const messageKey = `${type}:${callSessionId}`;

    if (messageKey === lastMessageKeyRef.current) return;
    lastMessageKeyRef.current = messageKey;

    // ── Incoming call arrives ────────────────────────────────
    if (type === 'INCOMING_CALL') {
      // Ignore incoming calls for 5 seconds after a call was ended/rejected
      // to prevent popup reappearing after ending a call
      if (Date.now() - lastResetTimeRef.current < 3000) return;

      const callerDisplayName = message.callerName?.trim() || 'AR Device';
      showCallNotification(callerDisplayName);

      deferStateUpdate(() => {
        setIncomingCall({
          callSessionId: message.callSessionId,
          callerId: message.callerId,
          callerName: message.callerName,
          VonageSessionId: message.VonageSessionId,
          VonageToken: message.VonageToken,
        });
        setIsAccepting(false);
        setShowIncomingModal(true);
      });
    }

    // ── Backend confirms acceptance: fetch JWT then navigate ─
    if (type === 'CALL_ACCEPTED' && isAccepting) {

      // Guard: only handle if this matches our pending accept
      if (
        incomingCall &&
        message.callSessionId !== incomingCall.callSessionId
      ) return;

      deferStateUpdate(() => {
        setShowIncomingModal(false);
      });

      const callerId = incomingCall?.callerId ?? 'unknown';
      const sessionId =
        message.VonageSessionId ?? incomingCall?.VonageSessionId ?? '';
      const token = message.VonageToken ?? incomingCall?.VonageToken;

      const navigateToCall = (resolvedToken: string) => {
        navigateToResolvedCall(
          callerId,
          message.callSessionId,
          sessionId,
          resolvedToken
        );
      };

      if (sessionId && token) {
        navigateToCall(token);
        return;
      }

      if (!sessionId) {
        deferStateUpdate(() => {
          setIsAccepting(false);
          setIncomingCall(null);
        });
        return;
      }

      const userId = getUserId() ?? '';
      getVideoToken(userId)
        .then((res) => {
          if (!res.isSuccess || !res.response?.token) {
            throw new Error(
              res.message || 'Failed to get video token'
            );
          }
          navigateToCall(res.response.token);
        })
        .catch(() => {
          // Token fetch failed — dismiss modal gracefully
          deferStateUpdate(() => {
            resetIncomingState();
          });
        });
    }

    // ── Caller cancelled before web accepted ─────────────────
    if (type === 'CALL_REJECTED' || type === 'CALL_ENDED' || type === 'MISSED_CALL') {
      deferStateUpdate(() => {
        resetIncomingState();
      });
    }
  }, [
    deferStateUpdate,
    incomingCall,
    isAccepting,
    lastMessage,
    navigateToResolvedCall,
    resetIncomingState,
    showCallNotification,
    showIncomingModal
  ]);

  const navigateToCall = useCallback((resolvedToken: string) => {
    if (!incomingCall) return;
    navigateToResolvedCall(
      incomingCall.callerId ?? 'unknown',
      incomingCall.callSessionId,
      incomingCall.VonageSessionId ?? '',
      resolvedToken
    );
  }, [incomingCall, navigateToResolvedCall]);

  // Send accept_call — modal stays open showing "Connecting…"
  const acceptCall = useCallback(() => {
    if (!incomingCall) return;

    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    if (isOffline) {
      showError('No internet connection. Unable to accept the call.');
      resetIncomingState();
      return;
    }

    setIsAccepting(true);

    const sent = sendMessage({
      action: 'accept_call',
      callSessionId: incomingCall.callSessionId,
      userId: getUserId() ?? '',
    });

    if (!sent) {
      console.warn('[useIncomingCall] accept_call not sent because WebSocket is not open');
      showError('Connection lost. Unable to accept the call.');
      resetIncomingState();
      return;
    }

    if (incomingCall.VonageSessionId && incomingCall.VonageToken) {
      navigateToCall(incomingCall.VonageToken);
    }
  }, [incomingCall, navigateToCall, sendMessage, showError, resetIncomingState]);

  // Send reject_call and immediately dismiss
  const rejectCall = useCallback(() => {
    if (!incomingCall) return;
    sendMessage({
      action: 'reject_call',
      callSessionId: incomingCall.callSessionId,
      userId: getUserId() ?? '',
    });
    resetIncomingState();
  }, [incomingCall, resetIncomingState, sendMessage]);

  return {
    incomingCall,
    showIncomingModal,
    isAccepting,
    acceptCall,
    rejectCall,
  };
};
